import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Package, Truck, CheckCircle, Clock, Wrench, Send, MapPin, User, Phone, Mail, ExternalLink, Download, Award } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import TrackingTimeline from '../components/TrackingTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: 'Solicitud Abierta', icon: Package, label: 'Solicitud Abierta' },
  { key: 'En Tránsito al Servicio Técnico', icon: Truck, label: 'En Tránsito' },
  { key: 'Entregado en Servicio Técnico', icon: MapPin, label: 'Entregado' },
  { key: 'Haciendo Comprobaciones', icon: Clock, label: 'Comprobaciones' },
  { key: 'En Reparación', icon: Wrench, label: 'En Reparación' },
  { key: 'Reparado', icon: CheckCircle, label: 'Reparado' },
  { key: 'Enviado al Cliente', icon: Send, label: 'Enviado' },
  { key: 'RMA Completado', icon: CheckCircle, label: 'Completado' }
];

const getStatusIndex = (status) => {
  const idx = STATUS_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
};

const WarrantyDetail = () => {
  const { t } = useTranslation();
  const { requestId } = useParams();
  const [warranty, setWarranty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [submittingTracking, setSubmittingTracking] = useState(false);

  const fetchWarranty = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/warranty/${requestId}`, { withCredentials: true });
      setWarranty(response.data);
    } catch (error) {
      console.error('Error fetching warranty:', error);
      toast.error('Error al cargar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarranty();
  }, [requestId]);

  const handleTrackingSubmit = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;
    
    setSubmittingTracking(true);
    try {
      await axios.put(
        `${API_URL}/api/warranty/${requestId}/tracking?tracking_number=${trackingNumber}`,
        {},
        { withCredentials: true }
      );
      toast.success('Número de seguimiento actualizado');
      fetchWarranty();
      setTrackingNumber('');
    } catch (error) {
      toast.error('Error al actualizar el tracking');
    } finally {
      setSubmittingTracking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  if (!warranty) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#52525B] mb-4">Solicitud no encontrada</p>
          <Link to="/warranty">
            <Button>{t('common.back')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentStatusIndex = getStatusIndex(warranty.status);

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="warranty-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link 
            to="/warranty" 
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#0A0A0A] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono text-sm text-[#52525B]">
              #{warranty.request_id?.slice(0, 12).toUpperCase()}
            </span>
          </div>
          <h1 className="text-2xl font-bold">{warranty.product_name}</h1>
        </div>

        {/* Status Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-[#E5E7EB] p-6 mb-6"
        >
          <h2 className="font-semibold mb-6">Estado de la Solicitud</h2>
          <div className="flex items-center justify-between overflow-x-auto pb-4">
            {STATUS_STEPS.map((step, index) => {
              const isCompleted = index < currentStatusIndex;
              const isCurrent = index === currentStatusIndex;
              const Icon = step.icon;
              
              return (
                <div key={step.key} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isCompleted ? 'bg-[#10B981]' :
                        isCurrent ? 'bg-[#0047FF]' : 'bg-[#E5E7EB]'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isCompleted || isCurrent ? 'text-white' : 'text-[#A1A1AA]'}`} />
                    </div>
                    <span className={`text-xs mt-2 text-center max-w-[80px] ${
                      isCurrent ? 'font-semibold text-[#0047FF]' : 'text-[#52525B]'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < STATUS_STEPS.length - 1 && (
                    <div className={`w-12 h-0.5 mx-1 ${
                      index < currentStatusIndex ? 'bg-[#10B981]' : 'bg-[#E5E7EB]'
                    }`}></div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Add Tracking (if status is Open) */}
        {warranty.status === 'Solicitud Abierta' && !warranty.tracking_number && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#0047FF]/5 border border-[#0047FF]/20 rounded-xl p-6 mb-6"
          >
            <h3 className="font-semibold mb-3">¿Ya has enviado el paquete?</h3>
            <p className="text-sm text-[#52525B] mb-4">
              Introduce el número de seguimiento de Correos para actualizar el estado de tu solicitud.
            </p>
            <form onSubmit={handleTrackingSubmit} className="flex gap-3">
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="PQ43B404AA015110128001N"
                className="font-mono flex-1"
                data-testid="tracking-input"
              />
              <Button
                type="submit"
                className="bg-[#0047FF] hover:bg-[#003BCC]"
                disabled={submittingTracking}
                data-testid="submit-tracking-btn"
              >
                {submittingTracking ? '...' : 'Actualizar'}
              </Button>
            </form>
          </motion.div>
        )}

        {/* Details Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Personal Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-[#E5E7EB] p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0047FF]/10 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-[#0047FF]" />
              </div>
              <h3 className="font-semibold">Datos Personales</h3>
            </div>
            <div className="space-y-3 text-sm">
              <p><span className="text-[#52525B]">Nombre:</span> {warranty.full_name}</p>
              <p>
                <span className="text-[#52525B]">Dirección:</span>{' '}
                {warranty.street_name} {warranty.street_number}
                {warranty.piso && `, Piso ${warranty.piso}`}
                {warranty.puerta && `, Puerta ${warranty.puerta}`}
              </p>
              <p><span className="text-[#52525B]">CP:</span> {warranty.postal_code}</p>
              <p><span className="text-[#52525B]">Localidad:</span> {warranty.municipio}, {warranty.comunidad}</p>
              <p><span className="text-[#52525B]">País:</span> {warranty.country}</p>
              <div className="flex items-center gap-2 pt-2">
                <Phone className="w-4 h-4 text-[#A1A1AA]" />
                <span>{warranty.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#A1A1AA]" />
                <span>{warranty.email}</span>
              </div>
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border border-[#E5E7EB] p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-[#8B5CF6]" />
              </div>
              <h3 className="font-semibold">Datos del Producto</h3>
            </div>
            <div className="space-y-3 text-sm">
              <p><span className="text-[#52525B]">Producto:</span> {warranty.product_name}</p>
              {warranty.serial_number && (
                <p><span className="text-[#52525B]">Nº Serie:</span> <span className="font-mono">{warranty.serial_number}</span></p>
              )}
              <p><span className="text-[#52525B]">Fecha compra:</span> {warranty.purchase_date}</p>
              <p><span className="text-[#52525B]">Lugar de compra:</span> {warranty.purchase_source}</p>
              <p><span className="text-[#52525B]">ID Pedido:</span> <span className="font-mono">{warranty.order_id}</span></p>
              {warranty.tracking_number && (
                <p><span className="text-[#52525B]">Tracking:</span> <span className="font-mono">{warranty.tracking_number}</span></p>
              )}
            </div>
          </motion.div>

          {/* Live Tracking Section - Only show if there's a tracking number */}
          {(warranty.tracking_number || warranty.outbound_tracking) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="md:col-span-2"
              data-testid="tracking-section"
            >
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#0047FF]/10 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#0047FF]" />
                  </div>
                  <h3 className="font-semibold">Seguimiento de Envío en Tiempo Real</h3>
                </div>
                
                {/* Inbound Tracking (customer to service center) */}
                {warranty.tracking_number && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-[#52525B] mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Envío al Servicio Técnico
                    </h4>
                    <TrackingTimeline 
                      trackingNumber={warranty.tracking_number} 
                      type="inbound"
                    />
                  </div>
                )}
                
                {/* Outbound Tracking (service center to customer) */}
                {warranty.outbound_tracking && (
                  <div className={warranty.tracking_number ? "pt-6 border-t" : ""}>
                    <h4 className="text-sm font-medium text-[#52525B] mb-3 flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Envío de Devolución
                    </h4>
                    <TrackingTimeline 
                      trackingNumber={warranty.outbound_tracking} 
                      type="outbound"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Problem Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl border border-[#E5E7EB] p-6 md:col-span-2"
          >
            <h3 className="font-semibold mb-3">Descripción del Problema</h3>
            <p className="text-[#52525B]">{warranty.problem_description}</p>
          </motion.div>

          {/* Repair Info (if available) */}
          {warranty.repair_description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-xl p-6 md:col-span-2"
            >
              <h3 className="font-semibold mb-3 text-[#10B981]">Trabajo Realizado</h3>
              <p className="text-[#52525B]">{warranty.repair_description}</p>
              {warranty.outbound_tracking && (
                <p className="mt-3">
                  <span className="text-[#52525B]">Tracking de envío:</span>{' '}
                  <span className="font-mono font-semibold">{warranty.outbound_tracking}</span>
                </p>
              )}
            </motion.div>
          )}

          {/* Certificate Download (only for completed RMAs) */}
          {warranty.status === 'RMA Completado' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-r from-[#0047FF]/5 to-[#8B5CF6]/5 border border-[#0047FF]/20 rounded-xl p-6 md:col-span-2"
              data-testid="certificate-section"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0047FF] to-[#8B5CF6] rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">Certificado de Reparación</h3>
                    <p className="text-sm text-[#52525B]">Descarga tu certificado oficial con sello digital y código QR de verificación</p>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const response = await axios.get(
                        `${API_URL}/api/warranty/${requestId}/certificate`,
                        { 
                          withCredentials: true,
                          responseType: 'blob'
                        }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `certificado_${requestId.slice(0, 8)}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Certificado descargado');
                    } catch (error) {
                      toast.error('Error al descargar el certificado');
                    }
                  }}
                  className="bg-gradient-to-r from-[#0047FF] to-[#8B5CF6] hover:opacity-90"
                  data-testid="download-certificate-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
              </div>
              <div className="mt-4 pt-4 border-t border-[#0047FF]/10">
                <p className="text-xs text-[#52525B]">
                  Este certificado acredita que tu dispositivo ha sido reparado por técnicos especializados 
                  y cuenta con <strong>3 meses de garantía</strong> sobre la reparación realizada.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WarrantyDetail;
